/// <amd-module name="Dashboards/Grid/WidgetSizeConverter" />

/** Size, as expressed in terms of discrete dashboard grid cells. */
export interface WidgetGridSize {
    columnSpan: number;
    rowSpan: number;
}

/** Size, expressed in pixels */
export interface WidgetSize {
    width: number;
    height: number;
}

/** Provides implementation secrets about Widget Sizing and Grid proportions. */
export class WidgetSizeConverter {
    /**
     * Cell width of the grid that is used to draw the widgets, this includes the border around the widget (i.e. this is the size of the div, border included)
     */
    public static readonly CellWidth: number = 160;

    /**
     * Cell height of the grid that is used to draw the widgets, this includes the border around the widget (i.e. this is the size of the div, border included)
     */
    public static readonly CellHeight: number = 160;

    /**
     * Cell gutter width between the cells that is used to draw the widget, this excludes the border around the widget (i.e. this is distance between widgets)
     */
    public static readonly CellMarginWidth: number = 10;

    /**
     * Cell gutter height between the cells that is used to draw the widget, this excludes the border around the widget  (i.e. this is distance between widgets)
     */
    public static readonly CellMarginHeight: number = 10;

    /**
     * Calculates a dimension in pixels, given widget cell size and grid dimensions
     * @returns size in pixels
     */
    private static CalculatePixelSize(cellCount: number, gridCellSize: number, gridMarginSize: number): number {
        // the dimensions of a multi-celled widget are a combination of space of the occupied cells AND the margins between those cells
        let marginCount = cellCount - 1;
        return gridCellSize * cellCount + gridMarginSize * marginCount;
    }

    /**
     * Calculates a dimension of grid from pixels, given widget pixel size and underlying grid dimensions
     * @returns size in pixels
     */
    private static CalculateGridSize(pixelSize: number, gridCellSize: number, gridMarginSize: number): number {
        return Math.ceil((pixelSize + gridMarginSize) / (gridMarginSize + gridCellSize));
    }

    /**
     * Converts widget column span into pixels
     * @returns width in pixels
     */
    public static ColumnsToPixelWidth(columnSpan: number): number {
        return this.CalculatePixelSize(columnSpan, WidgetSizeConverter.CellWidth, WidgetSizeConverter.CellMarginWidth);
    }

    /**
     * Converts widget row span into pixels
     * @returns height in pixels
     */
    public static RowsToPixelHeight(rowSpan: number): number {
        return this.CalculatePixelSize(rowSpan, WidgetSizeConverter.CellHeight, WidgetSizeConverter.CellMarginHeight);
    }

    public static ConvertToPixelSize(gridSize: WidgetGridSize): WidgetSize {
        return {
            width: WidgetSizeConverter.ColumnsToPixelWidth(gridSize.columnSpan),
            height: WidgetSizeConverter.RowsToPixelHeight(gridSize.rowSpan)
        };
    }

    public static ConvertToGridSize(gridSize: WidgetSize): WidgetGridSize {
        return {
            columnSpan: WidgetSizeConverter.CalculateGridSize(gridSize.width, WidgetSizeConverter.CellWidth, WidgetSizeConverter.CellMarginWidth),
            rowSpan: WidgetSizeConverter.CalculateGridSize(gridSize.height, WidgetSizeConverter.CellHeight, WidgetSizeConverter.CellMarginHeight)
        };
    }
}
